/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import axios from 'axios';
import type { TFunction } from 'i18next';
import { USER_SCHEDULED_FOR_DELETION } from '../constants/accountRecovery';
import { extractErrorMessage } from './errors';

export type ScheduledDeletionInfo = {
  deleteScheduledAt?: string;
  deleteRequestedAt?: string;
};

type ScheduledDeletionPayload = {
  code?: string;
  error?: string;
  message?: string;
  deleteScheduledAt?: string | number;
  deleteRequestedAt?: string | number;
  scheduledDeletionAt?: string | number;
  deletionDate?: string | number;
};

/** Login statuses the SSO returns for a soft-deleted account. */
const SCHEDULED_DELETION_HTTP_STATUSES = [403, 409];

function isScheduledDeletionCode(value?: string): boolean {
  return value === USER_SCHEDULED_FOR_DELETION;
}

function isScheduledDeletionMessage(value?: string): boolean {
  if (!value) return false;
  return /scheduled for deletion/i.test(value);
}

function toDateString(value?: string | number): string | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  return String(value);
}

/**
 * The error code is authoritative; the free-text message is only trusted when
 * the response status is one the SSO uses for a scheduled deletion.
 */
function fromPayload(
  payload: ScheduledDeletionPayload | null | undefined,
  httpStatus?: number,
): ScheduledDeletionInfo | null {
  if (!payload) return null;

  const messageMatches =
    httpStatus !== undefined &&
    SCHEDULED_DELETION_HTTP_STATUSES.includes(httpStatus) &&
    isScheduledDeletionMessage(payload.message);

  if (
    isScheduledDeletionCode(payload.code) ||
    isScheduledDeletionCode(payload.error) ||
    messageMatches
  ) {
    return {
      deleteScheduledAt: toDateString(
        payload.deleteScheduledAt ??
          payload.scheduledDeletionAt ??
          payload.deletionDate,
      ),
      deleteRequestedAt: toDateString(payload.deleteRequestedAt),
    };
  }

  return null;
}

/**
 * Detects USER_SCHEDULED_FOR_DELETION from credentials (Axios) or social-web reject shapes.
 */
export function parseScheduledDeletionError(
  error: unknown,
): ScheduledDeletionInfo | null {
  if (!error || typeof error !== 'object') {
    return null;
  }

  // Social web reject object: { error, deleteScheduledAt, deleteRequestedAt }
  const social = fromPayload(error as ScheduledDeletionPayload);
  if (social) {
    return social;
  }

  // Axios / CoreNetworking shapes
  if (axios.isAxiosError(error)) {
    const fromResponse = fromPayload(
      error.response?.data as ScheduledDeletionPayload | undefined,
      error.response?.status,
    );
    if (fromResponse) return fromResponse;
  }

  const withBody = error as {
    response?: { status?: number; data?: ScheduledDeletionPayload };
    errorBody?: ScheduledDeletionPayload;
  };
  const httpStatus = withBody.response?.status;

  return (
    fromPayload(withBody.response?.data, httpStatus) ??
    fromPayload(withBody.errorBody, httpStatus)
  );
}

/** Epoch values below this are seconds, not milliseconds (year 33658 in ms). */
const EPOCH_MS_THRESHOLD = 1e12;

/** Parses an SSO deletion timestamp (epoch s/ms or ISO); undefined when absent or invalid. */
export function parseDeletionDate(value?: string): Date | undefined {
  if (!value) return undefined;

  const trimmed = value.trim();
  const asNumber = Number(trimmed);
  const date =
    trimmed !== '' && Number.isFinite(asNumber)
      ? new Date(asNumber < EPOCH_MS_THRESHOLD ? asNumber * 1000 : asNumber)
      : new Date(trimmed);

  return Number.isNaN(date.getTime()) ? undefined : date;
}

function formatDisplayDate(date: Date, language: string): string {
  const locale = (language || 'en-GB').replace('_', '-');
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Formats the soft-delete deadline; undefined when the API gave no usable date. */
export function formatDeletionDate(
  deleteScheduledAt: string | undefined,
  language: string,
): string | undefined {
  const date = parseDeletionDate(deleteScheduledAt);
  return date ? formatDisplayDate(date, language) : undefined;
}

/** Snackbar message for a failed keep-account restore retry. */
export function describeRestoreError(
  error: unknown,
  t: TFunction,
  fallbackKey: string = 'loginPage.loginFailed',
): string {
  if (parseScheduledDeletionError(error)) {
    return t('keepAccount.restoreFailed');
  }

  return extractErrorMessage(
    error instanceof Error ? error : new Error(String(error)),
    // Dynamic key from login method fallbacks (typed i18n expects literals).
    t(fallbackKey as 'loginPage.loginFailed'),
  );
}
